import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { assetFieldConfig } from "../../config/assetFieldConfig";
import {
  apiGet,
  apiPost,
  apiPut,
} from "../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { ConfirmDialog } from "../../components/common/confirmDialog";
import { CustomButton } from "../../components/common/customButton";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { DynamicForm } from "../../components/common/DynamicForm";
import type { Asset } from "../../types/deal";
import ExtractedTenantsTable from "../tenant/ExtractedTenantTable";
import { useDealProvider } from "../../context/DealContext";
import {
  geocodingService,
  type GeocodeAddress,
} from "../../services/geocoding";
// import { placesCacheService } from '../../services/placesCache';
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "../../components/common/errorDialog";
import { normalizeAssetData } from "../../utils/dealValidation";
import { AssetMap } from "@/components/common/maps";
import {
  getGeocodeAddressKey,
  shouldAutoGeocode,
} from "../../utils/assetGeocoding";

const emptyAsset: Partial<Asset> = {
  asset_name: "",
  deal: null,
  address_1: "",
  address_2: "",
  city: "",
  postcode: "",
  country: [],
  longitude: undefined,
  latitude: undefined,
  currency: "",
  valuation_amount: undefined,
  valuation_date: undefined,
  acquisition_price: undefined,
  acquisition_date: undefined,
  sector: [],
  year_built: undefined,
  year_refurbished: undefined,
  count_buildings: undefined,
  count_floors: undefined,
  count_boxes: undefined,
  count_rooms: undefined,
  count_units: undefined,
  noi: undefined,
};

const CreateAsset = ({ asset: initialAsset = emptyAsset, isEdit = false }) => {
  const navigate = useNavigate();
  const { countries, sectors, currencies } = useReferenceLists();
  const [deals, setDeals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [asset, setAsset] = useState(initialAsset);
  const { setTenants, tenants } = useDealProvider();
  const [, setGeocodingLoading] = useState(false);
  const [geocodingMessage, setGeocodingMessage] = useState<{
    type: "success" | "error" | "info";
    text: string;
  } | null>(null);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  // Address last geocoded (or loaded with saved coordinates). Auto-geocoding only runs when
  // the address differs from this, so loading an asset never overwrites its saved pin and
  // geocoded/normalised addresses are not geocoded again.
  const lastGeocodedKeyRef = useRef<string | null>(
    initialAsset?.latitude != null && initialAsset?.longitude != null
      ? getGeocodeAddressKey(initialAsset)
      : null,
  );

  useEffect(() => {
    if (initialAsset) {
      setTenants(initialAsset.tenants || []);
    }
  }, []);

  useEffect(() => {
    apiGet("deals").then((response) => {
      if (response.success) {
        setDeals(response.data);
      } else {
        showErrorFromResponse(response);
      }
    });
  }, []);

  // Geocoding function
  const performGeocoding = useCallback(async (assetData: Partial<Asset>) => {
    const addressFields: GeocodeAddress = {
      asset_name: assetData.asset_name,
      address_1: assetData.address_1,
      address_2: assetData.address_2,
      city: assetData.city,
      postcode: assetData.postcode,
      country: Array.isArray(assetData.country)
        ? assetData.country[0]?.nicename
        : assetData.country,
    };

    // Check if we have enough address information
    const hasAddressInfo =
      addressFields.address_1 || addressFields.city || addressFields.postcode;
    if (!hasAddressInfo) {
      return;
    }

    // Record the attempt up front so a failing address is not retried automatically.
    const requestedKey = getGeocodeAddressKey(assetData);
    lastGeocodedKeyRef.current = requestedKey;

    if (!geocodingService.isConfigured()) {
      setGeocodingMessage({
        type: "error",
        text: "Google Maps API key not configured. Please contact your administrator.",
      });
      return;
    }

    setGeocodingLoading(true);
    setGeocodingMessage(null);

    try {
      const result = await geocodingService.geocodeAddress(addressFields);

      if (result.success && result.data) {
        const { data } = result;

        setAsset((prev) => {
          // Ignore a stale result if the address was edited while the request was in flight.
          if (getGeocodeAddressKey(prev) !== requestedKey) return prev;
          const next = {
            ...prev,
            latitude: data.latitude,
            longitude: data.longitude,
            // Update address fields with geocoded data if available
            ...(data.address_components && {
              address_1: data.address_components.address_1 || prev.address_1,
              address_2: data.address_components.address_2 || prev.address_2,
              city: data.address_components.city || prev.city,
              postcode: data.address_components.postcode || prev.postcode,
            }),
          };
          // The geocoder may normalise the address; don't geocode the normalised form again.
          lastGeocodedKeyRef.current = getGeocodeAddressKey(next);
          return next;
        });

        setGeocodingMessage({
          type: "success",
          text: "Address geocoded successfully! Coordinates and address details updated.",
        });
      } else {
        setGeocodingMessage({
          type: "error",
          text:
            (typeof result.message === "string"
              ? result.message
              : result.message?.message) || "Failed to geocode address",
        });
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      setGeocodingMessage({
        type: "error",
        text: "An error occurred while geocoding the address",
      });
    } finally {
      setGeocodingLoading(false);
    }
  }, []);

  const handleChange = (field: string, value: any) => {
    setAsset((prev) => {
      const updated = { ...prev, [field]: value };
      // When deal is deselected (null), also set deal_id to null
      if (
        field === "deal" &&
        (value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0))
      ) {
        (updated as any).deal_id = null;
      } else if (
        field === "deal" &&
        value &&
        typeof value === "object" &&
        value.id
      ) {
        // When deal is selected, set deal_id
        (updated as any).deal_id = value.id;
      }
      return updated;
    });
  };

  // Debounced geocoding effect: runs only when the address (see GEOCODE_TRIGGER_FIELDS)
  // has changed since it was last geocoded or loaded.
  useEffect(() => {
    if (!shouldAutoGeocode(asset, lastGeocodedKeyRef.current)) return;

    const timeoutId = setTimeout(() => {
      performGeocoding(asset);
    }, 2000); // 2 second debounce

    return () => clearTimeout(timeoutId);
  }, [
    asset.address_1,
    asset.address_2,
    asset.city,
    asset.postcode,
    asset.country,
    performGeocoding,
  ]);

  const handleSubmit = async () => {
    // replace the tenants object inside assets with the tenants object from the context
    const updatedAsset = { ...asset, tenants: tenants };
    // Normalize asset data before sending to API
    const normalizedAsset = normalizeAssetData(updatedAsset);
    try {
      if (isEdit && typeof asset.id !== "number") {
        showErrorFromResponse({
          success: false,
          status: 400,
          message: "Asset update failed: missing asset.id",
        });
        return;
      }
      const response = isEdit
        ? await apiPut("asset", asset.id as number, normalizedAsset)
        : await apiPost("assets", normalizedAsset);

      if (response && response.success) {
        navigate(isEdit ? `/assets/${asset.id}` : "/assets");
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      showErrorFromResponse({
        success: false,
        status: 500,
        message: "An unexpected error occurred while saving the asset.",
      });
    }
  };

  useEffect(() => {
    if (!initialAsset) {
      setTenants([]);
    }
  }, []);

  // Dragging the pin only moves it locally; the new coordinates are saved with the rest of
  // the form via Update Asset, so a drag never persists half-edited fields.
  const updateNewPosition = useCallback(
    (position: { lat: number; lng: number }) => {
      setAsset((prev) => ({
        ...prev,
        latitude: position.lat,
        longitude: position.lng,
      }));
      setGeocodingMessage({
        type: "info",
        text: `Pin moved. Click ${isEdit ? "Update Asset" : "Create Asset"} to save the new location.`,
      });
    },
    [isEdit],
  );

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ position: "sticky", zIndex: 10, marginBottom: "10px" }}>
        <SimpleToolbar customTop="0px">
          {isEdit && (
            <CustomButton
              text="Cancel"
              handleButtonPress={() => navigate(`/assets/${asset.id}`)}
              solid={false}
              isButtonDisabled={false}
              data-position="left"
            />
          )}
          {/* <CustomButton
            text="Geocode Address"
            handleButtonPress={() => performGeocoding(asset)}
            solid={false}
            isButtonDisabled={geocodingLoading || (!asset.address_1 && !asset.city && !asset.postcode)}
          /> */}
          <CustomButton
            text={isEdit ? "Update Asset" : "Create Asset"}
            handleButtonPress={handleSubmit}
            isButtonDisabled={!asset.asset_name}
          />
          {isEdit && (
            <CustomButton
              text="Delete Asset"
              isDeleteButton
              handleButtonPress={() => setDialogOpen(true)}
              isButtonDisabled={false}
              data-position="right"
            />
          )}
        </SimpleToolbar>

        <Box sx={{ width: "95%", margin: "0 auto", padding: 4 }}>
          <DynamicForm
            config={assetFieldConfig}
            values={asset}
            onChange={handleChange}
            optionsMap={{ deals, countries, sectors, currencies }}
            performGeocoding={performGeocoding}
          />
        </Box>
        {typeof asset.id === "number" && (
          <AssetMap
            assets={asset as Asset}
            accordions={undefined}
            showAssetNumbers={false}
            updateNewPosition={updateNewPosition}
          />
        )}
        <Box sx={{ maxWidth: "95%", margin: "0 auto", padding: 4, pt: 0 }}>
          <ExtractedTenantsTable
            assetId={typeof asset.id === "number" ? asset.id : null}
            editMode={true}
          />
        </Box>

        {typeof asset.id === "number" && (
          <ConfirmDialog
            open={dialogOpen}
            endpoint="asset"
            id={asset.id}
            title="Delete Asset"
            description="Are you sure you want to delete this asset?"
            onClose={() => setDialogOpen(false)}
            onSuccess={() => navigate("/assets")}
          />
        )}
      </Box>

      {/* Geocoding status snackbar */}
      <Snackbar
        open={!!geocodingMessage}
        autoHideDuration={6000}
        onClose={() => setGeocodingMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setGeocodingMessage(null)}
          severity={geocodingMessage?.type || "info"}
          sx={{ width: "100%" }}
        >
          {geocodingMessage?.text}
        </Alert>
      </Snackbar>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default CreateAsset;
